import React from "react";
import {
  MapPin,
  Phone,
  Clock,
  Instagram,
  MessageCircle,
  ExternalLink,
} from "lucide-react";
import { CLINIC, waLink, telLink } from "../lib/contact";

export const Contact = () => {
  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-20 sm:py-28 bg-[#E3F0E6]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#D1E2D6] text-[#4A5E4F] text-xs font-bold tracking-widest uppercase mb-5">
            Contáctanos
          </div>
          <h2 className="font-heading font-semibold text-[#2D3A30] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Visítanos o escríbenos
            <span className="text-[#F28C8C]"> cuando lo necesites.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4A5E4F] leading-relaxed">
            Estamos en el corazón de La Serena, listos para recibir a tu mascota
            con todo el cariño y profesionalismo que se merece.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Info */}
          <div className="space-y-5">
            <ContactCard
              icon={MapPin}
              label="Dirección"
              testid="contact-address"
              accent="#F28C8C"
              bg="#FFE4E6"
              action={{
                href: CLINIC.mapsUrl,
                label: "Ver en Google Maps",
                external: true,
              }}
            >
              <p className="text-[#2D3A30] font-semibold text-lg">
                {CLINIC.address}
              </p>
              <p className="text-[#4A5E4F]">
                {CLINIC.zip} · {CLINIC.region}
              </p>
            </ContactCard>

            <ContactCard
              icon={Phone}
              label="Teléfono"
              testid="contact-phone"
              accent="#84B891"
              bg="#E3F0E6"
              action={{
                href: telLink,
                label: "Llamar ahora",
              }}
            >
              <p className="text-[#2D3A30] font-semibold text-lg">
                {CLINIC.phoneDisplay}
              </p>
              <p className="text-[#4A5E4F]">Atención telefónica 24/7</p>
            </ContactCard>

            <ContactCard
              icon={Clock}
              label="Horario"
              testid="contact-hours"
              accent="#7BA8E0"
              bg="#E2EDFB"
            >
              <p className="text-[#2D3A30] font-semibold text-lg">
                Abierto las 24 horas
              </p>
              <p className="text-[#4A5E4F]">
                Todos los días, incluidos festivos.
              </p>
            </ContactCard>

            <ContactCard
              icon={Instagram}
              label="Instagram"
              testid="contact-instagram"
              accent="#C794D6"
              bg="#F1E4F6"
              action={{
                href: CLINIC.instagramUrl,
                label: "Seguir en Instagram",
                external: true,
              }}
            >
              <p className="text-[#2D3A30] font-semibold text-lg">
                {CLINIC.instagramHandle}
              </p>
              <p className="text-[#4A5E4F]">
                Mira a nuestros pacientes y novedades del día a día.
              </p>
            </ContactCard>

            {/* CTA pair */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold shadow-[0_10px_30px_rgba(37,211,102,0.4)] hover:-translate-y-0.5 transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
              <a
                href={telLink}
                data-testid="contact-call-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full bg-[#2D3A30] hover:bg-[#1d2820] text-white font-bold hover:-translate-y-0.5 transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                Llamar
              </a>
            </div>
          </div>

          {/* Map */}
          <div
            data-testid="contact-map"
            className="relative rounded-3xl overflow-hidden border border-[#D1E2D6] shadow-[0_14px_40px_rgba(45,58,48,0.10)] bg-white h-[420px] sm:h-[520px] lg:h-[640px] sticky top-28"
          >
            <iframe
              title="Ubicación Royal Pets Veterinaria"
              src={CLINIC.mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a
              href={CLINIC.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#2D3A30] font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Cómo llegar
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactCard = ({
  icon: Icon,
  label,
  children,
  action,
  accent,
  bg,
  testid,
}) => {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-3xl p-6 border border-[#D1E2D6] shadow-[0_8px_24px_rgba(45,58,48,0.05)] hover:shadow-[0_14px_36px_rgba(45,58,48,0.10)] hover:-translate-y-0.5 transition-all duration-300"
    >
      <div className="flex items-start gap-5">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: bg }}
        >
          <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-[#7B9681] mb-1">
            {label}
          </div>
          <div className="space-y-0.5">{children}</div>
          {action && (
            <a
              href={action.href}
              {...(action.external
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold hover:gap-2.5 transition-all"
              style={{ color: accent }}
            >
              {action.label}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
